import { EyeOutlined } from '@ant-design/icons';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { useMounted } from '@app/hooks/useMounted';
import { Space, Tooltip, Button } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { TablePaginationConfig } from 'antd';
import FilterComponent from '@app/components/TableFilter/TableFilter';
import { getReceiptListTableData } from '@app/api/finances.api';
import { InvoiceTableRow, Pagination, ReceiptTableRow } from '@app/models/finances/FinancesResponse';
import dayjs from 'dayjs';

const initialPagination: Pagination = {
  current: 1,
  pageSize: 5,
};

export type SelectOption = { label: string; value: string };

export const ReceiptTable: React.FC = () => {
  const [tableData, setTableData] = useState<{
    data: InvoiceTableRow[];
    pagination: Pagination;
    loading: boolean;
  }>({
    data: [],
    pagination: initialPagination,
    loading: false,
  });
  const navigate = useNavigate();
  const [filters, setFilters] = useState<{ [key: string]: string | null }>({});
  const [statusOptions, setStatusOptions] = useState<Record<string, SelectOption[]> | null>(null);
  const [departmentOptions, setDepartmentOptions] = useState<Record<string, SelectOption[]> | null>(null);
  const [taskTypeOptions, setTypeTypeOptions] = useState<Record<string, SelectOption[]> | null>(null);
  const [customerTypeOptions, setCustomerTypeOptions] = useState<Record<string, SelectOption[]> | null>(null);
  const [cashierOptions, setCashierOptions] = useState<Record<string, SelectOption[]> | null>(null);
  const [cashierLocationOptions, setCashierLocaOptions] = useState<Record<string, SelectOption[]> | null>(null);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<Record<string, SelectOption[]> | null>(null);

  const normalize = (arr?: { label: string; value: string | number }[]) => {
    if (!Array.isArray(arr)) return [];
    return arr.map((i) => ({
      label: i.label,
      value: String(i.value),
    }));
  };

  // Single consolidated effect for both filter options and initial data fetch
  useEffect(() => {
    let isMountedLocal = true;

    const loadData = async () => {
      try {
        const res = await getReceiptListTableData({});
        if (!isMountedLocal) return;

        // Load filter options
        setStatusOptions({ status: normalize(res.filter_options.status_options) });
        setDepartmentOptions({ department_id: normalize(res.filter_options.department_options) });
        setTypeTypeOptions({ referenceable_type: normalize(res.filter_options.referenceable_type_options) });
        setCustomerTypeOptions({ partner_type: normalize(res.filter_options.partner_type_options) });
        setCashierOptions({ cashier_id: normalize(res.filter_options.cashier_options) });
        setCashierLocaOptions({ cashier_location_id: normalize(res.filter_options.cashier_location_options) });
        setPaymentMethodOptions({ date_related_to: normalize(res.filter_options.payment_method_options) });

        // Set initial table data
        setTableData({
          data: res.data,
          pagination: {
            current: res.pagination.current,
            pageSize: res.pagination.pageSize,
            total: res.pagination.total,
          },
          loading: false,
        });
      } catch (error) {
        console.error(error);
        if (isMountedLocal) {
          setTableData({ data: [], pagination: initialPagination, loading: false });
        }
      }
    };

    loadData();

    return () => {
      isMountedLocal = false;
    };
  }, []);

  // Single effect for refetching on filters or pagination change
  useEffect(() => {
    setTableData((prev) => ({ ...prev, loading: true }));

    getReceiptListTableData({ ...initialPagination, ...filters })
      .then((res) => {
        setTableData({
          data: res.data,
          pagination: {
            current: res.pagination.current,
            pageSize: res.pagination.pageSize,
            total: res.pagination.total,
          },
          loading: false,
        });
      })
      .catch((error: any) => {
        console.error(error);
        setTableData({ data: [], pagination: initialPagination, loading: false });
      });
  }, [filters]);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setTableData((prev) => ({ ...prev, loading: true }));
    getReceiptListTableData({
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 5,
      ...filters,
    })
      .then((res) => {
        setTableData({
          data: res.data,
          pagination: {
            current: res.pagination.current,
            pageSize: res.pagination.pageSize,
            total: res.pagination.total,
          },
          loading: false,
        });
      })
      .catch((error: any) => {
        console.error(error);
        setTableData({ data: [], pagination: initialPagination, loading: false });
      });
  };

  const showTotal = (total: number, range: [number, number]): React.ReactNode => {
    return (
      <span style={{ fontSize: '14px', marginRight: '20px', color: '#adadad' }}>
        {`Showing ${range[0]} - ${range[1]} of ${total} Invoices`}
      </span>
    );
  };

  const searchData: {
    name: string;
    placeholder: string;
    type: 'text' | 'dropdown' | 'daterange';
    options?: Record<string, SelectOption[]>;
  }[] = useMemo(
    () => [
      { name: 'receipt_number', placeholder: 'Receipt No', type: 'text' },
      { name: 'partner', placeholder: 'Customer', type: 'text' },
      {
        name: 'department_id',
        placeholder: 'Department',
        type: 'dropdown',
        options: departmentOptions ?? undefined,
      },
      { name: 'referenceable_type', placeholder: 'Task Type', type: 'dropdown', options: taskTypeOptions ?? undefined },
      { name: 'referenceable_code', placeholder: 'Task Code', type: 'text' },
      {
        name: 'partner_type',
        placeholder: 'Customer Type',
        type: 'dropdown',
        options: customerTypeOptions ?? undefined,
      },
      { name: 'cashier_id', placeholder: 'Cashier', type: 'dropdown', options: cashierOptions ?? undefined },
      {
        name: 'cashier_location_id',
        placeholder: 'Cashier Location',
        type: 'dropdown',
        options: cashierLocationOptions ?? undefined,
      },
      {
        name: 'payment_method',
        placeholder: 'Payment Method',
        type: 'dropdown',
        options: paymentMethodOptions ?? undefined,
      },
      { name: 'status', placeholder: 'Status', type: 'dropdown', options: statusOptions ?? undefined },
      {
        name: 'date_range',
        placeholder: 'Select Date Range',
        type: 'daterange',
      },
    ],
    [departmentOptions, taskTypeOptions, customerTypeOptions, cashierOptions, cashierLocationOptions, statusOptions],
  );

  const columns: ColumnsType<ReceiptTableRow> = [
    {
      title: 'Receipt Number',
      dataIndex: 'receipt_code',
      key: 'receipt_code',
    },
    {
      title: 'Invoice Number',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
    },
    {
      title: 'Customer Name',
      dataIndex: 'partner_name',
      key: 'partner_name',
    },
    {
      title: 'Department Name',
      dataIndex: 'department_name',
      key: 'department_name',
    },
    {
      title: 'Task Type',
      dataIndex: 'referenceable_type_label',
      key: 'referenceable_type_label',
    },
    {
      title: 'Task Code',
      dataIndex: 'referenceable_code',
      key: 'referenceable_code',
    },
    {
      title: 'Invoice Status',
      dataIndex: 'invoice_status',
      key: 'invoice_status',
    },
    {
      title: 'Total',
      dataIndex: 'amount',
      key: 'amount',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View">
            <Button
              type="text"
              onClick={() =>
                navigate('/view-receipt', {
                  state: {
                    receiptId: record.id,
                    invoice_number: record.invoice_number,
                    invoice_status: record.invoice_status,
                    receipt_code: record.receipt_code,
                  },
                })
              }
              icon={<EyeOutlined />}
              style={{ color: '#1890ff' }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleFilters = (values: { [key: string]: any }) => {
    const newFilters: { [key: string]: string | null } = { ...values };

    if (values.date_range) {
      newFilters.from = values.date_range[0] ? dayjs(values.date_range[0]).format('YYYY-MM-DD') : null;
      newFilters.to = values.date_range[1] ? dayjs(values.date_range[1]).format('YYYY-MM-DD') : null;
      delete newFilters.date_range;
    }

    setFilters(newFilters);
  };

  return (
    <BaseCard id="basic-table" title="Receipts" padding="1.25rem">
      <FilterComponent data={searchData} onFilter={handleFilters} />
      <BaseTable
        columns={columns}
        dataSource={tableData.data}
        pagination={{
          current: tableData.pagination.current,
          pageSize: tableData.pagination.pageSize,
          total: tableData.pagination.total,
          showTotal,
        }}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: true, scrollToFirstRowOnChange: true }}
      />
    </BaseCard>
  );
};
